import React from 'react';
import { Link } from 'react-router-dom';

export interface ContentAreaTab {
  id: string;
  label: string;
  path: string;
}

interface ContentAreaProps {
  title: string;
  tabs: ContentAreaTab[];
  activeTabId: string;
  children?: React.ReactNode;
}

/**
 * ContentArea - Page header with title and horizontal tabs (prototype pattern).
 *
 * Tabs navigate via Link to their path. The active tab is determined by
 * matching the activeTabId against the current tab.
 */
export function ContentArea({ title, tabs, activeTabId, children }: ContentAreaProps) {
  return (
    <div className="flex-1 flex flex-col">
      {/* Page Header with Tabs - matches Dashboard spacing (PageLayout provides outer padding) */}
      <div className="border-b border-white/10 bg-[#111010]">
        <div className="pb-3 md:pb-4">
          <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold mb-4 md:mb-6">
            {title}
          </h1>

          {/* Horizontal Tabs - scrollable on small screens */}
          <div className="flex gap-1 -mb-px overflow-x-auto scrollbar-none">
            {tabs.map((tab) => {
              const isActive = activeTabId === tab.id;

              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  className={`
                    px-4 md:px-6 py-2.5 md:py-3 font-medium text-sm rounded-t-lg transition-all relative whitespace-nowrap shrink-0 no-underline
                    ${isActive
                      ? 'text-text-primary bg-[#221F1F]'
                      : 'text-text-tertiary hover:text-text-secondary hover:bg-white/5'
                    }
                  `}
                >
                  {tab.label}

                  {/* Active Tab Bottom Border */}
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-rollops-primary" />
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content - no extra padding; matches Dashboard (PageLayout provides outer padding) */}
      <div className="flex-1 bg-[#111010] overflow-auto scrollbar-styled">
        {children}
      </div>
    </div>
  );
}
